"use client";

import { cn } from "@nyntax/design-system/lib/utils";
import { rankItem } from "@tanstack/match-sorter-utils";
import {
  type ColumnDef,
  type ExpandedState,
  type FilterFn,
  type FilterFnOption,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type TableOptions,
  useReactTable,
} from "@tanstack/react-table";
import { type ReactNode, useCallback, useState } from "react";
import { DataTableContext } from "./data-table-context";

const EMPTY_SORTING: SortingState = [];

function readStoredSorting(key: string | undefined): SortingState | null {
  if (!key || typeof window === "undefined") {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as SortingState) : null;
  } catch {
    return null;
  }
}

function readStoredPageIndex(key: string | undefined): number | null {
  if (!key || typeof window === "undefined") {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    const parsed = Number.parseInt(raw, 10);
    return Number.isNaN(parsed) ? null : parsed;
  } catch {
    return null;
  }
}

// biome-ignore lint/suspicious/noExplicitAny: filter works on any row type via getValue
const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value);
  addMeta({ itemRank });
  return itemRank.passed;
};

function enforceSingleExpand(
  prev: ExpandedState,
  next: ExpandedState
): ExpandedState {
  if (next === true) {
    return next;
  }
  const prevKeys =
    typeof prev === "object" ? Object.keys(prev).filter((k) => prev[k]) : [];
  const nextKeys = Object.keys(next).filter((k) => next[k]);
  const newKey = nextKeys.find((k) => !prevKeys.includes(k));
  if (newKey) {
    return { [newKey]: true };
  }
  return next;
}

interface DataTableProps<TData> {
  /** Allow only one row expanded at a time. Defaults to true (multiple). */
  allowMultipleExpanded?: boolean;
  children: ReactNode;
  className?: string;
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  defaultSorting?: SortingState;
  enableRowSelection?: boolean | ((row: { original: TData }) => boolean);
  enableSortingRemoval?: boolean;
  /**
   * Custom global filter function applied when searching client-side.
   * Default: fuzzy matching via @tanstack/match-sorter-utils (typo-tolerant).
   */
  globalFilterFn?: FilterFnOption<TData>;
  /** Called when the user changes page via pagination controls. */
  onPageIndexChange?: (pageIndex: number) => void;
  /** Called when the search value changes via DataTableToolbarSearch. */
  onSearchChange?: (value: string) => void;
  /** Total page count for server-side (manual) pagination. */
  pageCount?: number;
  /** Controlled page index (0-based). Makes pagination externally controlled. */
  pageIndex?: number;
  pageSize?: number;
  /**
   * Total row count across all pages for server-side (manual) pagination.
   * Required for accurate range labels (e.g. "40-52 of 52") and the
   * range-info footer; otherwise TanStack falls back to the current page's
   * data length.
   */
  rowCount?: number;
  /**
   * Controlled search value. When provided, DataTable stores it in context
   * for DataTableToolbarSearch to display. For client-side tables (no
   * pageCount), it is also applied as TanStack's globalFilter.
   */
  searchValue?: string;
  /** Pass additional TanStack Table options (merged with internal defaults) */
  tableOptions?: Partial<
    Omit<TableOptions<TData>, "columns" | "data" | "getCoreRowModel">
  >;
  /**
   * When set, sorting state persists to localStorage under this key, so it
   * survives remounts/reloads instead of resetting to defaultSorting.
   */
  sortingStorageKey?: string;
  /**
   * When set (and pagination is uncontrolled), page index persists to
   * localStorage under this key, so it survives remounts/reloads.
   */
  pageIndexStorageKey?: string;
}

function DataTable<TData>({
  children,
  className,
  columns,
  data,
  pageSize = 13,
  pageCount,
  rowCount,
  pageIndex: controlledPageIndex,
  onPageIndexChange,
  defaultSorting = EMPTY_SORTING,
  enableSortingRemoval = false,
  enableRowSelection = true,
  allowMultipleExpanded = true,
  searchValue: controlledSearchValue,
  onSearchChange,
  globalFilterFn,
  tableOptions,
  sortingStorageKey,
  pageIndexStorageKey,
}: DataTableProps<TData>) {
  const isControlled = controlledPageIndex !== undefined;
  const isSearchControlled = controlledSearchValue !== undefined;
  const isManualPagination = pageCount !== undefined;

  const [internalPagination, setInternalPaginationState] =
    useState<PaginationState>(() => ({
      pageIndex: readStoredPageIndex(pageIndexStorageKey) ?? 0,
      pageSize,
    }));
  const setInternalPagination: OnChangeFn<PaginationState> = useCallback(
    (updater) => {
      setInternalPaginationState((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        if (pageIndexStorageKey && typeof window !== "undefined") {
          try {
            window.localStorage.setItem(
              pageIndexStorageKey,
              String(next.pageIndex)
            );
          } catch {
            // ignore storage failures (e.g. private browsing quota)
          }
        }
        return next;
      });
    },
    [pageIndexStorageKey]
  );
  const [sorting, setSortingState] = useState<SortingState>(
    () => readStoredSorting(sortingStorageKey) ?? defaultSorting
  );
  const setSorting: OnChangeFn<SortingState> = useCallback(
    (updater) => {
      setSortingState((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        if (sortingStorageKey && typeof window !== "undefined") {
          try {
            window.localStorage.setItem(sortingStorageKey, JSON.stringify(next));
          } catch {
            // ignore storage failures (e.g. private browsing quota)
          }
        }
        return next;
      });
    },
    [sortingStorageKey]
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [internalSearch, setInternalSearch] = useState("");

  const handleExpandedChange: OnChangeFn<ExpandedState> = useCallback(
    (updater) => {
      setExpanded((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        if (allowMultipleExpanded) {
          return next;
        }
        return enforceSingleExpand(prev, next);
      });
    },
    [allowMultipleExpanded]
  );

  const resolvedPagination: PaginationState = isControlled
    ? { pageIndex: controlledPageIndex, pageSize }
    : internalPagination;

  const resolvedSearch = isSearchControlled
    ? controlledSearchValue
    : internalSearch;

  const handleSearchChange = useCallback(
    (value: string) => {
      if (isSearchControlled) {
        onSearchChange?.(value);
      } else {
        setInternalSearch(value);
      }
    },
    [isSearchControlled, onSearchChange]
  );

  const handlePaginationChange: OnChangeFn<PaginationState> = useCallback(
    (updater) => {
      if (isControlled) {
        const next =
          typeof updater === "function" ? updater(resolvedPagination) : updater;
        onPageIndexChange?.(next.pageIndex);
      } else {
        setInternalPagination(updater);
      }
    },
    [isControlled, onPageIndexChange, resolvedPagination]
  );

  const applyGlobalFilter = !isManualPagination;

  const table = useReactTable({
    columns,
    data,
    defaultColumn: {
      minSize: 0,
      size: 0,
    },
    enableRowSelection,
    enableSortingRemoval,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(isManualPagination && {
      manualPagination: true,
      pageCount,
      ...(rowCount !== undefined && { rowCount }),
    }),
    ...(applyGlobalFilter && {
      globalFilterFn: globalFilterFn ?? fuzzyFilter,
    }),
    onExpandedChange: handleExpandedChange,
    onGlobalFilterChange: (updater) => {
      const next =
        typeof updater === "function" ? updater(resolvedSearch) : updater;
      handleSearchChange(next);
    },
    onPaginationChange: handlePaginationChange,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    state: {
      expanded,
      ...(applyGlobalFilter && { globalFilter: resolvedSearch }),
      pagination: resolvedPagination,
      rowSelection,
      sorting,
    },
    ...tableOptions,
  });

  return (
    <DataTableContext
      value={{
        table,
        searchValue: resolvedSearch,
        setSearchValue: handleSearchChange,
      }}
    >
      <div className={cn("flex w-full flex-col", className)}>{children}</div>
    </DataTableContext>
  );
}

export type { DataTableProps };
export { DataTable };
